import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Contact from '../components/Contact';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  const services = {
    'web-development': {
      title: 'Enterprise Web Development',
      subtitle: 'Scalable, high-performance web applications engineered with modern frameworks',
      description: 'We build enterprise-grade web applications that scale with your business. Our team leverages cutting-edge technologies to create responsive, secure, and high-performance solutions tailored to your specific needs.',
      heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'React, Next.js, Vue.js Development',
        'Responsive & Mobile-First Design',
        'Performance Optimization & SEO',
        'Enterprise Security & Compliance',
        'Scalable Architecture & Cloud Integration',
        'API Development & Integration'
      ],
      technologies: ['React', 'Next.js', 'Vue.js', 'Node.js', 'TypeScript', 'Tailwind CSS', 'PostgreSQL', 'MongoDB'],
      process: [
        {
          step: '01',
          title: 'Discovery & Planning',
          description: 'We start by understanding your business goals, target audience, and technical requirements. Through collaborative workshops, we define the project scope, create user personas, and establish success metrics.',
          image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
          tasks: ['Requirements gathering', 'Stakeholder interviews', 'Technical feasibility analysis', 'Project roadmap creation']
        },
        {
          step: '02',
          title: 'Design & Prototyping',
          description: 'Our design team creates intuitive user interfaces and experiences. We develop wireframes, interactive prototypes, and design systems that ensure consistency and usability across all platforms.',
          image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80',
          tasks: ['UI/UX design', 'Prototype development', 'User testing', 'Design system creation']
        },
        {
          step: '03',
          title: 'Development & Integration',
          description: 'Using agile methodologies, we build your web application with clean, maintainable code. We integrate third-party services, APIs, and ensure seamless functionality across devices and browsers.',
          image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80',
          tasks: ['Frontend development', 'Backend integration', 'API development', 'Third-party integrations']
        },
        {
          step: '04',
          title: 'Testing & Quality Assurance',
          description: 'Rigorous testing ensures your application performs flawlessly. We conduct unit tests, integration tests, performance testing, and security audits to deliver a robust, bug-free solution.',
          image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
          tasks: ['Automated testing', 'Performance optimization', 'Security audits', 'Cross-browser testing']
        },
        {
          step: '05',
          title: 'Deployment & Launch',
          description: 'We deploy your application to production with zero downtime. Our deployment process includes cloud infrastructure setup, CI/CD pipeline configuration, and comprehensive monitoring.',
          image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
          tasks: ['Cloud deployment', 'CI/CD setup', 'Monitoring configuration', 'Launch support']
        },
        {
          step: '06',
          title: 'Support & Maintenance',
          description: 'Post-launch, we provide ongoing support and maintenance. We monitor performance, fix issues, implement updates, and continuously optimize your application for peak performance.',
          image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80',
          tasks: ['24/7 monitoring', 'Bug fixes', 'Performance updates', 'Feature enhancements']
        }
      ],
      benefits: [
        { icon: '⚡', title: 'Lightning Fast', description: 'Optimized performance with sub-second load times' },
        { icon: '🔒', title: 'Secure', description: 'Enterprise-grade security with regular audits' },
        { icon: '📱', title: 'Responsive', description: 'Perfect experience on all devices' },
        { icon: '📈', title: 'Scalable', description: 'Grows with your business needs' }
      ]
    },
    'mobile-development': {
      title: 'Native & Cross-Platform Mobile',
      subtitle: 'Premium mobile applications for iOS and Android platforms',
      description: 'We develop native and cross-platform mobile applications that deliver exceptional user experiences. Our mobile solutions are built for performance, scalability, and seamless integration with your existing systems.',
      heroImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 18H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'iOS & Android Native Development',
        'React Native Cross-Platform Solutions',
        'Flutter App Development',
        'App Store Optimization',
        'Push Notifications & Analytics',
        'API Integration & Backend Services'
      ],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'iOS', 'Android', 'Firebase', 'GraphQL'],
      process: [
        {
          step: '01',
          title: 'Strategy & Planning',
          description: 'We analyze your target market, competitor apps, and define your app\'s unique value proposition. We create detailed user flows, feature prioritization, and technical architecture plans.',
          image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&q=80',
          tasks: ['Market research', 'User persona development', 'Feature prioritization', 'Tech stack selection']
        },
        {
          step: '02',
          title: 'Design & UX',
          description: 'Mobile-first design approach ensures intuitive navigation and engaging user experiences. We create pixel-perfect designs following iOS and Android design guidelines.',
          image: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=800&q=80',
          tasks: ['Mobile UI/UX design', 'Design mockups', 'Interactive prototypes', 'User testing']
        },
        {
          step: '03',
          title: 'Development',
          description: 'We build your mobile app using native or cross-platform frameworks based on your requirements. Our code follows best practices for maintainability and performance.',
          image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80',
          tasks: ['Native/Cross-platform development', 'Backend integration', 'API integration', 'Third-party SDKs']
        },
        {
          step: '04',
          title: 'Testing & QA',
          description: 'Comprehensive testing across devices and OS versions ensures your app works flawlessly. We test functionality, performance, security, and user experience.',
          image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80',
          tasks: ['Device testing', 'Beta testing', 'Performance testing', 'Security testing']
        },
        {
          step: '05',
          title: 'App Store Submission',
          description: 'We handle the entire app store submission process, including store listing optimization, screenshots, descriptions, and compliance with platform guidelines.',
          image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&q=80',
          tasks: ['Store listing optimization', 'App submission', 'Review process', 'Compliance checks']
        },
        {
          step: '06',
          title: 'Launch & Growth',
          description: 'Post-launch support includes monitoring, analytics setup, crash reporting, and continuous improvements based on user feedback and analytics data.',
          image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
          tasks: ['Analytics setup', 'Crash monitoring', 'User feedback', 'Feature updates']
        }
      ],
      benefits: [
        { icon: '🚀', title: 'Fast Performance', description: 'Native-like performance with smooth animations' },
        { icon: '💰', title: 'Cost Effective', description: 'Cross-platform saves time and resources' },
        { icon: '📊', title: 'Analytics Ready', description: 'Built-in analytics and tracking' },
        { icon: '🔄', title: 'Easy Updates', description: 'Over-the-air updates for seamless maintenance' }
      ]
    },
    'intelligent-automation-ai': {
      title: 'Intelligent Automation & AI',
      subtitle: 'Custom LLM workflows and automation built for your operations',
      description: 'We design and integrate intelligent automation—from custom LLM workflows and retrieval-augmented processes to orchestration—that replaces manual bottlenecks with reliable, measurable throughput your teams can trust.',
      heroImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'Custom LLM and agentic workflow design',
        'RAG and enterprise knowledge integration',
        'Process automation with clear human handoffs',
        'API orchestration and integration',
        'Guardrails, evaluation, and observability',
        'Pilot-to-production rollout with your teams'
      ],
      technologies: ['Python', 'OpenAI API', 'Azure OpenAI', 'LangChain', 'Vector databases', 'Node.js', 'Kubernetes'],
      process: [
        { step: '01', title: 'Discovery & use cases', description: 'We map high-friction workflows, data boundaries, and success metrics and select pilot problems where automation delivers clear ROI.', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80', tasks: ['Stakeholder workshops', 'Process mapping', 'Data source review', 'Compliance checkpoints'] },
        { step: '02', title: 'Architecture & governance', description: 'We design retrieval, tools, and policy so models stay on-brand and on-policy.', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80', tasks: ['Solution architecture', 'Model selection', 'Security review', 'Governance model'] },
        { step: '03', title: 'Build & integrate', description: 'We implement workflows and connectors tied to your systems of record.', image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80', tasks: ['Implementation', 'API integration', 'Testing', 'Documentation'] },
        { step: '04', title: 'Evaluate & harden', description: 'Structured evaluation, tuning, and monitoring for drift and failure modes.', image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80', tasks: ['Benchmarks', 'Latency and cost tuning', 'Alerting'] },
        { step: '05', title: 'Deploy & enable', description: 'Controlled rollout with training and runbooks.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', tasks: ['Production cutover', 'Training', 'Runbooks'] },
        { step: '06', title: 'Improve & expand', description: 'Measure outcomes and iterate on new workflows.', image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80', tasks: ['Analytics', 'Roadmap reviews', 'Continuous improvement'] }
      ],
      benefits: [
        { icon: '⚡', title: 'Faster throughput', description: 'Less manual work on repeatable tasks' },
        { icon: '🧠', title: 'Grounded answers', description: 'Retrieval aligned to your data' },
        { icon: '📊', title: 'Measurable', description: 'Clear KPIs from pilot to scale' },
        { icon: '🔒', title: 'Controlled', description: 'Guardrails and audit-friendly patterns' }
      ]
    },
    'cybersecurity': {
      title: 'Resilient Cybersecurity',
      subtitle: 'Zero trust architecture and proactive readiness',
      description: 'We move organizations beyond static perimeter thinking with zero-trust patterns, identity-first access, logging and detection, and practical hardening so security keeps pace with how you actually work.',
      heroImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'Zero-trust network and identity design',
        'Endpoint and server hardening baselines',
        'Logging, SIEM forwarding, and detection use cases',
        'Vulnerability management and patching programs',
        'Incident response planning and tabletop exercises',
        'Compliance alignment (SOC 2, ISO-oriented practices)'
      ],
      technologies: ['Microsoft Entra ID', 'Okta', 'EDR', 'SIEM', 'Terraform', 'AWS Security', 'Azure Defender'],
      process: [
        { step: '01', title: 'Assess & prioritize', description: 'Current controls, crown-jewel assets, and realistic threat model.', image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80', tasks: ['Asset inventory', 'Control review', 'Gap analysis', 'Roadmap'] },
        { step: '02', title: 'Identity & access', description: 'MFA, least privilege, conditional access, and privileged access hygiene.', image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80', tasks: ['IdP configuration', 'Role design', 'PAM guidance', 'SSO patterns'] },
        { step: '03', title: 'Harden & segment', description: 'Baselines for endpoints, servers, and cloud accounts; network segmentation where it matters.', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80', tasks: ['CIS-style baselines', 'Firewall / NSG rules', 'Secrets hygiene', 'Encryption'] },
        { step: '04', title: 'Detect & respond', description: 'Centralized logging, alerts, and playbooks your team can run.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', tasks: ['Log sources', 'Detection rules', 'IR runbooks', 'Tabletops'] },
        { step: '05', title: 'Validate', description: 'Testing and third-party-style checks to prove controls work.', image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80', tasks: ['Vuln scans', 'Phishing simulations', 'Control testing', 'Findings triage'] },
        { step: '06', title: 'Operate & improve', description: 'Ongoing tuning, metrics, and quarterly security reviews.', image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80', tasks: ['Patch cadence', 'Metric dashboards', 'Roadmap updates', 'Training'] }
      ],
      benefits: [
        { icon: '🛡️', title: 'Resilience', description: 'Defense in depth that matches real workflows' },
        { icon: '🔍', title: 'Visibility', description: 'Logs and detections you can act on' },
        { icon: '✓', title: 'Governance', description: 'Policies and ownership, not one-off tools' },
        { icon: '📋', title: 'Audit-ready', description: 'Evidence and process for stakeholders' }
      ]
    },
    'cloud-infrastructure': {
      title: 'Cloud 3.0 Solutions',
      subtitle: 'Migrations, hybrid operations, and cost-optimized cloud on AWS and Azure',
      description: 'We deliver seamless migrations and disciplined cloud operations across AWS, Azure, and hybrid environments—landing zones, connectivity, Kubernetes where appropriate, FinOps discipline, and reliability practices so scale does not come at the cost of surprise bills or outages.',
      heroImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'Landing zones: AWS, Azure, and hybrid connectivity',
        'Workload migration and modernization paths',
        'Kubernetes, containers, and GitOps-style delivery',
        'FinOps: visibility, rightsizing, commitments where sensible',
        'Observability, SLOs, and incident-ready operations',
        'Backup, disaster recovery, and resilience testing'
      ],
      technologies: ['AWS', 'Azure', 'Terraform', 'Kubernetes', 'Docker', 'GitHub Actions', 'Argo CD', 'Prometheus'],
      process: [
        {
          step: '01',
          title: 'Assess & plan',
          description: 'We inventory workloads, dependencies, and constraints—then define a migration or modernization sequence with clear success criteria and risk controls.',
          image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
          tasks: ['Discovery workshops', 'TCO view', 'Target architecture', 'Wave planning']
        },
        {
          step: '02',
          title: 'Landing zone & IaC',
          description: 'Accounts, networking, identity, and guardrails expressed as code so environments stay consistent and auditable.',
          image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
          tasks: ['Landing zone design', 'Terraform / Bicep / CloudFormation', 'RBAC model', 'Policy as code']
        },
        {
          step: '03',
          title: 'Migrate & integrate',
          description: 'Lift-and-shift, replatform, or refactor in waves—with validation at each boundary.',
          image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80',
          tasks: ['Migration execution', 'Data sync', 'Cutover planning', 'Rollback readiness']
        },
        {
          step: '04',
          title: 'Platform & delivery',
          description: 'CI/CD, container platforms, and developer experience that match your team maturity.',
          image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=800&q=80',
          tasks: ['Pipeline design', 'K8s or PaaS patterns', 'Secrets management', 'Environments']
        },
        {
          step: '05',
          title: 'Observe & protect',
          description: 'Metrics, logs, traces, and cloud-native security controls wired into how you operate.',
          image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80',
          tasks: ['Dashboards & alerts', 'SLO definition', 'Cloud security baselines', 'Backup verification']
        },
        {
          step: '06',
          title: 'Optimize & govern',
          description: 'Cost reviews, capacity planning, and continuous improvement loops with your stakeholders.',
          image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
          tasks: ['FinOps reviews', 'Rightsizing', 'Reserved / savings plans', 'Roadmap']
        }
      ],
      benefits: [
        { icon: '☁️', title: 'Hybrid-ready', description: 'AWS, Azure, and on-prem as one story' },
        { icon: '💰', title: 'Cost-aware', description: 'FinOps discipline—not surprise invoices' },
        { icon: '🔐', title: 'Secure baseline', description: 'Guardrails built into the platform' },
        { icon: '⚡', title: 'Reliable delivery', description: 'Automation and observability you can run' }
      ]
    },
    'managed-it': {
      title: 'Managed IT Excellence',
      subtitle: '24/7 operations so your team stays focused on the mission',
      description: 'High-uptime support for the systems your business runs on—monitoring, patching, service desk coordination, and vendor liaison—so leadership spends less time on server status and more on outcomes.',
      heroImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        '24/7 monitoring and alerting with clear escalation',
        'Patch, vulnerability, and lifecycle management',
        'Backup verification and recovery drills',
        'End-user and service desk coordination',
        'Vendor and cloud provider liaison',
        'Reporting tailored for IT and business stakeholders'
      ],
      technologies: ['Microsoft 365', 'Intune', 'RMM platforms', 'ITSM', 'Datadog', 'PagerDuty', 'AWS/Azure support tooling'],
      process: [
        { step: '01', title: 'Onboard & baseline', description: 'Inventory, access, and documentation so nothing critical is invisible.', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80', tasks: ['CMDB / asset list', 'Access model', 'Criticality tiers', 'SLA definition'] },
        { step: '02', title: 'Monitor & alert', description: 'Signals that mean something—not noise—with on-call coverage aligned to your hours.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', tasks: ['Monitoring setup', 'Alert routing', 'Runbooks', 'Health checks'] },
        { step: '03', title: 'Maintain & patch', description: 'Predictable windows, testing, and rollback for changes.', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80', tasks: ['Patch cadence', 'Change management', 'Testing', 'Communication'] },
        { step: '04', title: 'Support users', description: 'Tickets triaged with clear priorities and communication.', image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80', tasks: ['Service desk', 'Knowledge base', 'SLA tracking', 'Satisfaction feedback'] },
        { step: '05', title: 'Protect data', description: 'Backups tested; recovery paths rehearsed.', image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80', tasks: ['Backup jobs', 'Restore tests', 'Retention policy', 'DR alignment'] },
        { step: '06', title: 'Review & plan', description: 'Monthly or quarterly reviews with a forward roadmap.', image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80', tasks: ['Incident review', 'Capacity trends', 'Risk register', 'Improvement backlog'] }
      ],
      benefits: [
        { icon: '🕐', title: 'Always-on', description: 'Coverage when your systems cannot sleep' },
        { icon: '📋', title: 'Predictable', description: 'Processes you can audit and rely on' },
        { icon: '🤝', title: 'Extension of you', description: 'We work alongside your internal IT' },
        { icon: '📈', title: 'Improving', description: 'Metrics and reviews, not firefighting alone' }
      ]
    },
    'edtech-platforms': {
      title: 'Revolutionary EdTech Platforms',
      subtitle: 'Innovative educational technology solutions',
      description: 'We create transformative EdTech platforms that enhance learning experiences through gamification, AI-powered personalization, and data-driven insights. Our solutions empower educators and engage learners.',
      heroImage: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'Learning Management Systems',
        'Gamification & Engagement Tools',
        'AI-Powered Personalization',
        'Analytics & Progress Tracking',
        'Assessment & Evaluation Tools',
        'Virtual Classroom Solutions'
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'AI/ML', 'WebRTC', 'Cloud Storage', 'TensorFlow', 'Python'],
      process: [
        {
          step: '01',
          title: 'Learning Objectives & Curriculum',
          description: 'We work with educators to understand learning objectives, curriculum requirements, and pedagogical approaches. We design the platform structure to support effective teaching and learning.',
          image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800&q=80',
          tasks: ['Curriculum analysis', 'Learning objectives definition', 'Pedagogy research', 'Platform requirements']
        },
        {
          step: '02',
          title: 'Gamification & Engagement Design',
          description: 'We design engaging learning experiences through gamification elements, interactive content, and reward systems. Our goal is to make learning fun and motivating for students.',
          image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
          tasks: ['Gamification design', 'Reward system design', 'Interactive content planning', 'Engagement metrics']
        },
        {
          step: '03',
          title: 'Platform Development',
          description: 'We build the EdTech platform with features like LMS, virtual classrooms, assessment tools, and analytics dashboards. The platform is designed for scalability and accessibility.',
          image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
          tasks: ['LMS development', 'Virtual classroom features', 'Assessment tools', 'Analytics implementation']
        },
        {
          step: '04',
          title: 'AI & Personalization',
          description: 'We integrate AI/ML capabilities to personalize learning paths, provide adaptive assessments, and generate insights. The system learns from student behavior to improve recommendations.',
          image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80',
          tasks: ['AI model integration', 'Personalization engine', 'Adaptive learning', 'Recommendation system']
        },
        {
          step: '05',
          title: 'Content Integration & Testing',
          description: 'We integrate educational content, multimedia resources, and conduct extensive testing with educators and students. User feedback is incorporated to refine the platform.',
          image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
          tasks: ['Content management', 'Multimedia integration', 'Beta testing', 'User feedback integration']
        },
        {
          step: '06',
          title: 'Launch & Continuous Improvement',
          description: 'Post-launch, we monitor platform usage, learning outcomes, and user satisfaction. We continuously improve features, add new capabilities, and optimize based on data insights.',
          image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80',
          tasks: ['Platform launch', 'Usage analytics', 'Learning outcome tracking', 'Feature enhancements']
        }
      ],
      benefits: [
        { icon: '🎓', title: 'Personalized Learning', description: 'AI-driven personalized learning paths' },
        { icon: '🎮', title: 'Engaging Experience', description: 'Gamification keeps students motivated' },
        { icon: '📊', title: 'Data-Driven Insights', description: 'Comprehensive analytics for educators' },
        { icon: '🌐', title: 'Accessible', description: 'Works on any device, anywhere' }
      ]
    },
    'ui-ux-design': {
      title: 'Premium UI/UX Design',
      subtitle: 'Research-led interfaces that convert and delight',
      description: 'We craft intuitive, accessible interfaces grounded in user research, prototyping, and design systems—so your product feels cohesive from first click to daily use.',
      heroImage: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 19L19 12L12 5L5 12L12 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'User research & journey mapping',
        'Wireframes & interactive prototypes',
        'Design systems & component libraries',
        'Accessibility (WCAG-oriented) reviews',
        'Handoff for engineering with specs',
        'Usability testing & iteration'
      ],
      technologies: ['Figma', 'Adobe XD', 'Miro', 'Storybook', 'Design tokens', 'React', 'Tailwind CSS'],
      process: [
        { step: '01', title: 'Discover', description: 'We align on users, jobs-to-be-done, and success metrics.', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80', tasks: ['Stakeholder workshops', 'Analytics review', 'Competitive scan'] },
        { step: '02', title: 'Define', description: 'Information architecture and flows that reduce friction.', image: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&q=80', tasks: ['IA', 'User flows', 'Content strategy'] },
        { step: '03', title: 'Design', description: 'High-fidelity UI with a scalable system mindset.', image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80', tasks: ['Visual design', 'Components', 'Motion where it helps'] },
        { step: '04', title: 'Validate', description: 'Test with real users and refine before build.', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80', tasks: ['Prototype tests', 'Findings synthesis', 'Iteration'] },
        { step: '05', title: 'Deliver', description: 'Specs, assets, and collaboration with engineering.', image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80', tasks: ['Redlines', 'Tokens', 'QA support'] },
        { step: '06', title: 'Evolve', description: 'Measure usage and improve post-launch.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', tasks: ['Analytics', 'A/B hooks', 'Backlog'] }
      ],
      benefits: [
        { icon: '✨', title: 'Cohesive', description: 'One system across web and mobile' },
        { icon: '🎯', title: 'Goal-driven', description: 'Design tied to outcomes, not trends' },
        { icon: '♿', title: 'Inclusive', description: 'Accessibility considered from the start' },
        { icon: '🤝', title: 'Build-ready', description: 'Smooth handoff to your developers' }
      ]
    },
    'technology-consulting': {
      title: 'Strategic Technology Consulting',
      subtitle: 'Clarity for complex technology decisions',
      description: 'From technical audits and architecture reviews to digital transformation roadmaps, we help leadership prioritize investments, reduce risk, and sequence change in a way teams can execute.',
      heroImage: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80',
      icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M12 20V10M18 20V4M6 20v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      features: [
        'Technology strategy & roadmaps',
        'Architecture and platform assessments',
        'Vendor and build-vs-buy guidance',
        'Security and compliance gap reviews',
        'Cost and TCO modeling',
        'Program governance and KPIs'
      ],
      technologies: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Enterprise architecture', 'Agile', 'ITIL patterns'],
      process: [
        { step: '01', title: 'Frame', description: 'Objectives, constraints, and stakeholders defined up front.', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80', tasks: ['Executive alignment', 'Scope', 'Success metrics'] },
        { step: '02', title: 'Assess', description: 'Current systems, skills, and risks documented honestly.', image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80', tasks: ['Interviews', 'Artifact review', 'Risk register'] },
        { step: '03', title: 'Options', description: 'We present realistic paths with trade-offs—not slide decks only.', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80', tasks: ['Scenarios', 'Effort / impact', 'Dependencies'] },
        { step: '04', title: 'Plan', description: 'Phased roadmap with owners and decision points.', image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&q=80', tasks: ['Waves', 'Budget view', 'Change plan'] },
        { step: '05', title: 'Enable', description: 'Workshops and playbooks so teams adopt the plan.', image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80', tasks: ['Training', 'Templates', 'Steer-co cadence'] },
        { step: '06', title: 'Measure', description: 'Track value and adjust the roadmap quarterly.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80', tasks: ['KPIs', 'Reviews', 'Refresh'] }
      ],
      benefits: [
        { icon: '🧭', title: 'Direction', description: 'Decisions you can explain to the board' },
        { icon: '⚖️', title: 'Balance', description: 'Speed, cost, and risk in one view' },
        { icon: '📐', title: 'Practical', description: 'Roadmaps your teams can ship against' },
        { icon: '🔒', title: 'Risk-aware', description: 'Security and compliance factored in early' }
      ]
    }
  };

  const service = services[serviceId];

  if (!service) {
    return (
      <>
        <Navbar />
        <div style={{ paddingTop: '100px', textAlign: 'center', minHeight: '60vh' }}>
          <h1>Service Not Found</h1>
          <button onClick={() => navigate('/services')} className="btn-primary" style={{ marginTop: '20px' }}>
            Back to Services
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title={`${service.title} - CodeRidge`}
        description={service.description}
        keywords={`${service.title}, ${service.technologies.join(', ')}, CodeRidge services`}
        url={`https://coderidge.in/services/${serviceId}`}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="service-hero-section">
        <div className="service-hero-background" style={{ backgroundImage: `url(${service.heroImage})` }}></div>
        <div className="service-hero-overlay"></div>
        <div className="container">
          <div className="service-hero-content">
            <div className="service-hero-icon">{service.icon}</div>
            <h1 className="service-hero-title">{service.title}</h1>
            <p className="service-hero-subtitle">{service.subtitle}</p>
            <p className="service-hero-description">{service.description}</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="service-benefits-section">
        <div className="container">
          <div className="service-benefits-grid">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="service-benefit-card">
                <div className="service-benefit-icon">{benefit.icon}</div>
                <h3 className="service-benefit-title">{benefit.title}</h3>
                <p className="service-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="service-features-detail-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">What We Offer</span>
            <h2 className="section-title">Key Features</h2>
            <p className="section-description">
              Capabilities mapped to outcomes—security, scale, cost discipline, and teams that can run what we ship.
            </p>
          </div>
          <div className="features-grid-detail">
            {service.features.map((feature, index) => (
              <div key={index} className="feature-card-detail">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="feature-check-icon">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="feature-text-detail">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Process</span>
            <h2 className="section-title">How We Work</h2>
            <p className="section-description">
              Transparent phases, clear owners, and checkpoints so delivery never outruns governance or operations.
            </p>
          </div>
          <div className="service-process-steps">
            {service.process.map((step, index) => (
              <div key={index} className="service-process-step">
                <div className="process-step-image-wrapper">
                  <img src={step.image} alt={step.title} className="process-step-image" />
                  <div className="process-step-number">{step.step}</div>
                </div>
                <div className="process-step-content">
                  <h3 className="process-step-title">{step.title}</h3>
                  <p className="process-step-description">{step.description}</p>
                  <ul className="process-step-tasks">
                    {step.tasks.map((task, taskIndex) => (
                      <li key={taskIndex}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="service-technologies-detail-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Tech Stack</span>
            <h2 className="section-title">Technologies We Use</h2>
            <p className="section-description">
              Proven tools across cloud, data, automation, and applications—chosen for fit, not fashion.
            </p>
          </div>
          <div className="technologies-grid-detail">
            {service.technologies.map((tech, index) => (
              <div key={index} className="tech-card-detail">
                <span className="tech-name-detail">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta-detail-section">
        <div className="container">
          <div className="service-cta-content">
            <h2 className="service-cta-title">Book a free infrastructure audit</h2>
            <p className="service-cta-description">Tell us your priorities—automation, security, cloud, or day-to-day operations—and we will follow up to schedule a no-obligation review and next steps.</p>
            <button onClick={() => navigate('/contact')} className="btn-primary btn-large">
              Request an audit
            </button>
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </>
  );
};

export default ServiceDetailPage;
